import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Edit, 
  RotateCcw, 
  History, 
  Sparkles,
  MessageSquare,
  Building2,
  User,
  HelpCircle,
  Lock
} from "lucide-react";
import PasswordVerifyDialog from "./PasswordVerifyDialog";
import PromptEditDialog from "./PromptEditDialog";
import PromptHistoryDialog from "./PromptHistoryDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category metadata
const CATEGORY_INFO = {
  LEAD_ENRICH: {
    icon: MessageSquare,
    label: "Lead Enrichment",
    description: "Comprehensive company and contact analysis with 10 data categories",
    color: "from-blue-600 to-indigo-600"
  },
  COMPANY_ENRICH: {
    icon: Building2,
    label: "Company Enrichment",
    description: "Basic company information with 6 essential fields",
    color: "from-purple-600 to-pink-600"
  },
  CONTACT_ENRICH: {
    icon: User,
    label: "Contact Enrichment",
    description: "Professional profile data with 4 key fields",
    color: "from-green-600 to-teal-600"
  }
};

const PromptManager = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [actionType, setActionType] = useState(null); // 'edit', 'restore', 'rollback'

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await axios.get(`${API}/admin/prompts`, {
        withCredentials: true
      });
      setPrompts(response.data.prompts);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching prompts:", error);
      toast.error("Failed to fetch prompts");
      setLoading(false);
    }
  };

  const handleEditClick = (prompt) => {
    setSelectedPrompt(prompt);
    setActionType('edit');
    setShowPasswordDialog(true);
  };

  const handleRestoreClick = (prompt) => {
    setSelectedPrompt(prompt);
    setActionType('restore');
    setShowPasswordDialog(true);
  };

  const handleHistoryClick = (prompt) => {
    setSelectedPrompt(prompt);
    setShowHistoryDialog(true);
  };

  const handlePasswordVerified = () => {
    setShowPasswordDialog(false);
    
    if (actionType === 'edit') {
      setShowEditDialog(true);
    } else if (actionType === 'restore') {
      handleRestoreDefaults();
    }
  };

  const handleRestoreDefaults = async () => {
    if (!selectedPrompt) return;
    
    try {
      const password = sessionStorage.getItem('verified_password');
      const response = await axios.post(
        `${API}/admin/prompts/${selectedPrompt.id}/restore-default`,
        { password },
        { withCredentials: true }
      );
      
      toast.success("Prompt restored to defaults");
      fetchPrompts();
    } catch (error) {
      console.error("Error restoring defaults:", error);
      toast.error(error.response?.data?.detail || "Failed to restore defaults");
    }
  };

  const handleSaveChanges = async (newFeatures, changeReason) => {
    if (!selectedPrompt) return;
    
    try {
      const password = sessionStorage.getItem('verified_password');
      const response = await axios.post(
        `${API}/admin/prompts/${selectedPrompt.id}/save`,
        {
          password,
          features: newFeatures,
          change_reason: changeReason
        },
        { withCredentials: true }
      );
      
      toast.success(`Prompt updated! Version ${response.data.result.version_number} created`);
      setShowEditDialog(false);
      fetchPrompts();
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error(error.response?.data?.detail || "Failed to save changes");
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading prompts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            AI Prompt Manager
          </h2>
          <p className="text-slate-600 mt-1">
            Manage and customize AI enrichment prompts for OpenAI models
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            <Lock className="w-3 h-3 mr-1" />
            Admin Only
          </Badge>
          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
            OpenAI GPT-4o
          </Badge>
        </div>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">About Prompt Management</p>
            <p className="text-blue-700">
              Edit prompt features like temperature and max tokens to customize AI behavior. 
              Base prompts are locked for stability. All changes are versioned and can be rolled back. 
              Password verification required before editing.
            </p>
          </div>
        </div>
      </div>

      {/* Prompt Cards Grid */}
      <div className="grid grid-cols-1 gap-6">
        {prompts.map((prompt) => {
          const categoryInfo = CATEGORY_INFO[prompt.category];
          const IconComponent = categoryInfo.icon;
          
          return (
            <Card key={prompt.id} className="overflow-hidden">
              {/* Category Header with Gradient */}
              <div className={`bg-gradient-to-r ${categoryInfo.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{categoryInfo.label}</h3>
                      <p className="text-white/80 text-sm mt-1">{categoryInfo.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                      Version {prompt.version_number}
                    </Badge>
                    <Badge variant="secondary" className="bg-white/20 text-white border-white/30 text-xs">
                      {prompt.status}
                    </Badge>
                  </div>
                </div>
              </div>

              <CardContent className="p-6">
                {/* Current Features */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Current Settings</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Temperature</p>
                      <p className="text-lg font-semibold text-slate-900">{prompt.features.temperature}</p>
                    </div>
                    
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Max Tokens</p>
                      <p className="text-lg font-semibold text-slate-900">{prompt.features.max_tokens}</p>
                    </div>
                    
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Style</p>
                      <p className="text-lg font-semibold text-slate-900 capitalize">{prompt.features.response_style}</p>
                    </div>
                    
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Confidence</p>
                      <p className="text-lg font-semibold text-slate-900">{(prompt.features.confidence_threshold * 100).toFixed(0)}%</p>
                    </div>
                    
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Required Fields</p>
                      <p className="text-lg font-semibold text-slate-900">{prompt.features.fields_required?.length || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Last Updated */}
                {prompt.updated_at && (
                  <div className="text-xs text-slate-500 mb-4">
                    Last updated: {new Date(prompt.updated_at).toLocaleString()}
                    {prompt.updated_by && <span> by admin</span>}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleEditClick(prompt)}
                    className="flex-1"
                    variant="default"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Features
                  </Button>
                  
                  <Button
                    onClick={() => handleRestoreClick(prompt)}
                    variant="outline"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restore Defaults
                  </Button>
                  
                  <Button
                    onClick={() => handleHistoryClick(prompt)}
                    variant="outline"
                  >
                    <History className="w-4 h-4 mr-2" />
                    History
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Dialogs */}
      {showPasswordDialog && (
        <PasswordVerifyDialog
          open={showPasswordDialog}
          onClose={() => setShowPasswordDialog(false)}
          onVerified={handlePasswordVerified}
        />
      )}

      {showEditDialog && selectedPrompt && (
        <PromptEditDialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          prompt={selectedPrompt}
          onSave={handleSaveChanges}
        />
      )}

      {showHistoryDialog && selectedPrompt && (
        <PromptHistoryDialog
          open={showHistoryDialog}
          onClose={() => setShowHistoryDialog(false)}
          prompt={selectedPrompt}
          onRollback={() => {
            setShowHistoryDialog(false);
            fetchPrompts();
          }}
        />
      )}
    </div>
  );
};

export default PromptManager;
